// src/pages/ApplicationChampionship/index.tsx
import { useCallback, useState } from 'react';
import { Text, Flex, useToast, Button } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { PageContent } from 'src/components/PageContent';
import { SectionBox } from 'src/components/SectionBox';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { breadcrumb } from './breadcrumb';
import { GetParamsObject } from '@/model/query-params';
import { useFiltersForm } from '@/utils/useFiltersForm';
import { ChakraFeatherIcon } from '@/components/ChakraFeatherIcon';
import { ConfirmationModal } from '@/components/ConfirmationModal';
import { Championship } from '@/model';
import { ChampionshipFilters } from './types';
import { ChampionshipTable } from './components/ChampionshipTable/ChampionshipTable';
import { ChampionshipFilterBox } from './components/ChampionshipFilterBox';
import { useChampionships, useDeleteChampionshipMutation } from '@/services/queries/championship';
import { ChampionshipCreateModal } from './components/ChampionshipCreateModal';
import { ChampionshipEditModal } from './components/ChampionshipEditModal';

export const ApplicationChampionship = () => {
  const { t } = useTranslation();
  const toast = useToast();

  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const [selectedItem, setSelectedItem] = useState<Championship | undefined>(undefined);

  const filtersForm = useFiltersForm<ChampionshipFilters>({
    defaultValues: {
      name: '',
      is_active: '',
      is_filtered: '',
      start_date: '',
      end_date: '',
    },
  });
  const filterData = filtersForm.watch();

  const [sorting, setSorting] = useState<{ field: string; desc: boolean }>({
    field: 'created_at',
    desc: true,
  });
  const [pagination, setPagination] = useState<{ pageNumber: number; pageSize: number }>({
    pageNumber: 1,
    pageSize: 10,
  });

  const params = GetParamsObject(
    filterData,
    pagination.pageNumber,
    pagination.pageSize,
    sorting.desc ? 'desc' : 'asc',
    sorting.field,
  );

  const { data: itemData, isLoading, forceRefetch } = useChampionships(params);
  const deleteMutation = useDeleteChampionshipMutation();

  const itemsData = itemData?.data || [];

  const onHandleDelete = (item: Championship) => {
    setSelectedItem(item);
    setIsConfirmModalOpen(true);
  };

  const onHandleDeleteConfirm = () => {
    if (!selectedItem?.uuid) return;

    setIsConfirmModalOpen(false);

    deleteMutation.mutate(selectedItem.uuid, {
      onSuccess: () => {
        toast({
          title: t('pages.application_championship.components.championship.application_championship_delete_success'),
          status: 'success',
        });
        forceRefetch();
        setSelectedItem(undefined);
      },
      onError: (resp: any) => {
        setSelectedItem(undefined);
        toast({
          status: 'error',
          title: (resp && resp.message) || t('pages.application_championship.components.championship.application_championship_delete_error'),
        });
      },
    });
  };

  const onHandleEdit = (item: Championship) => {
    setSelectedItem(item);
    setIsEditModalOpen(true);
  };

  const onHandleEditDone = useCallback(() => {
    setIsEditModalOpen(false);
    setSelectedItem(undefined);
    forceRefetch();
  }, [forceRefetch]);

  const onHandleCreateDone = useCallback(() => {
    setIsCreateModalOpen(false);
    forceRefetch();
  }, [forceRefetch]);

  return (
    <PageContent title={t('pages.application_championship.components.championship.application_championship_title')} breadcrumbItems={breadcrumb}>
      <SectionBox bodyProps={{ gap: 'small' }}>
        <Flex direction={{ base: 'column', md: 'row' }} justifyContent={{ md: 'space-between' }} gap={4}>
          <Text fontWeight='bold' color='brand.500'>
            {t('pages.application_championship.components.championship.application_championship_label')}
          </Text>

          <Button
            display='flex'
            alignItems='center'
            gap={2}
            onClick={() => setIsCreateModalOpen(true)}
            width={{ base: '100%', md: 'initial' }}
          >
            <ChakraFeatherIcon name='plus-circle' />
            {t('pages.application_championship.components.championship.application_championship_add_button')}
          </Button>
        </Flex>

        <SectionBox bodyProps={{ gap: 'small' }}>
          <ChampionshipFilterBox filtersForm={filtersForm} />
        </SectionBox>

        <Flex direction='column' gap='deka' mt={8}>
          <ChampionshipTable
            data={itemsData}
            isLoading={isLoading}
            sorting={sorting}
            pageNumber={pagination.pageNumber}
            pageSize={pagination.pageSize}
            totalPages={itemData?.meta.pages || 0}
            totalItems={itemData?.meta.total || 0}
            onSortingChange={(field, desc) => setSorting({ field, desc })}
            onPaginationChange={(pageNumber, pageSize) => setPagination({ pageNumber, pageSize })}
            onEditClick={item => onHandleEdit(item)}
            onDeleteClick={item => onHandleDelete(item)}
          />
        </Flex>
      </SectionBox>

      {selectedItem && (
        <ChampionshipEditModal
          isOpen={!!isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedItem(undefined);
          }}
          item={selectedItem}
          onDone={onHandleEditDone}
        />
      )}

      <ChampionshipCreateModal
        isOpen={!!isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onDone={onHandleCreateDone}
      />

      <ConfirmationModal
        isOpen={isConfirmModalOpen}
        onClose={() => {
          setIsConfirmModalOpen(false);
          setSelectedItem(undefined);
        }}
        onConfirm={onHandleDeleteConfirm}
        isLoading={false}
        title={t('pages.application_championship.components.championship.application_championship_delete_confirm_title')}
        description={
          <Text>
            {t('pages.application_championship.components.championship.application_championship_delete_confirm_description_start')}
            <Text as='span' fontWeight='bold'>
              "{selectedItem?.name}"
            </Text>
            {t('pages.application_championship.components.championship.application_championship_delete_confirm_description_end')}
          </Text>
        }
      />

      <CenteredSpinner isLoading={deleteMutation.isPending} />
    </PageContent>
  );
};
